import { useId } from 'react'
import type { Family, Monster, Rarity } from '../game/types'
import { FAMILIES } from '../game/families'
import { hashSeed, mulberry32 } from '../game/generator'
import { evolutionsOf, stageOf } from '../game/progression'

// Avatar vectoriel 100% déterministe: même seed → même monstre. Chaque famille
// garde sa SILHOUETTE reconnaissable, mais désormais le seed fait varier une
// COULEUR D'ACCENT (rotation de teinte), des motifs, des pics et des ornements —
// et la RARETÉ ajoute des éléments visibles (gemme, ailes, aura, couronne). À
// l'évolution, le corps grandit et gagne des parts cohérentes.
//
// NB: ce rendu enrichi diverge désormais du portage Solidity (MonsterRenderer.sol) —
// le re-portage on-chain est en roadmap (cf. README).

// Rythme d'idle par famille: Base vibre, Bitcoin respire lentement.
const IDLE_DURATION: Record<Family, string> = {
  base: '1.5s',
  meme: '1.9s',
  defi: '2.1s',
  ethereum: '2.4s',
  stable: '2.9s',
  bitcoin: '3.5s',
}

const RARITY_LEVEL: Record<Rarity, number> = { common: 0, rare: 1, epic: 2, legendary: 3 }

interface Props {
  monster: Monster
  size?: number
  animated?: boolean
  className?: string
}

// --- couleurs : rotation de teinte pour une couleur d'accent unique ----------
function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  const l = (max + min) / 2
  const d = max - min
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  return [h, s, l]
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0
  let g = 0
  let b = 0
  if (h < 60) [r, g, b] = [c, x, 0]
  else if (h < 120) [r, g, b] = [x, c, 0]
  else if (h < 180) [r, g, b] = [0, c, x]
  else if (h < 240) [r, g, b] = [0, x, c]
  else if (h < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const to = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

function shiftHue(hex: string, deg: number, dl = 0): string {
  const [h, s, l] = hexToHsl(hex)
  return hslToHex((h + deg + 360) % 360, s, Math.max(0, Math.min(1, l + dl)))
}

interface Gene {
  eye: number
  mouth: number
  accent: string
  accentDark: string
  accentLight: string
  accent2: string // 2e accent (raretés hautes) — teinte encore différente
  eyeColor: string
  pattern: number // 0 pois, 1 rayures, 2 losanges
  patternDensity: number // 3..5 éléments
  spikes: number // 2..4
  spikeStyle: number // 0 pointu, 1 courbé, 2 trapu
  wingStyle: number // 0 anguleux, 1 membraneux, 2 plumeté
  crownStyle: number // 0 pointes, 1 arrondi, 2 royal
  jitterX: number // largeur
  jitterY: number // hauteur
  bodyR: number[] // rayons par point du contour → SILHOUETTE unique
  bodyAspect: number // largeur/hauteur du corps
}

// Style de silhouette par famille: anguleux (géométrique) ou organique (courbe),
// rayon de base, aspect, et fourchette du nombre de points du contour.
const BODY_STYLE: Record<Family, { smooth: boolean; r: number; aspect: number; pts: [number, number]; irregular: number }> = {
  bitcoin: { smooth: false, r: 43, aspect: 1.0, pts: [6, 7], irregular: 0.16 },
  ethereum: { smooth: false, r: 47, aspect: 0.78, pts: [4, 6], irregular: 0.14 },
  defi: { smooth: false, r: 43, aspect: 0.96, pts: [5, 7], irregular: 0.2 },
  base: { smooth: true, r: 43, aspect: 1.0, pts: [7, 9], irregular: 0.18 },
  meme: { smooth: true, r: 45, aspect: 1.02, pts: [7, 10], irregular: 0.36 },
  stable: { smooth: true, r: 43, aspect: 0.85, pts: [7, 9], irregular: 0.2 },
}

// Chaque monstre tire un « gène » du seed: couleur(s) d'accent, variantes
// d'ornements ET une SILHOUETTE de corps propre (rayons par point du contour).
// Deux monstres d'une même famille/rareté ont donc un corps distinct.
function makeGene(seed: string, family: Family): Gene {
  const fam = FAMILIES[family]
  const rng = mulberry32(hashSeed(seed + ':look'))
  const eye = Math.floor(rng() * 3)
  const mouth = Math.floor(rng() * 3)
  const sign = rng() < 0.5 ? -1 : 1
  const deg = sign * (40 + rng() * 110) // ±40..150°
  const accent = shiftHue(fam.color, deg)
  const deg2 = -sign * (60 + rng() * 90)
  const st = BODY_STYLE[family]
  const nPts = st.pts[0] + Math.floor(rng() * (st.pts[1] - st.pts[0] + 1))
  const bodyR = Array.from({ length: nPts }, () => 1 - st.irregular / 2 + rng() * st.irregular)
  return {
    eye,
    mouth,
    accent,
    accentDark: shiftHue(fam.color, deg, -0.22),
    accentLight: shiftHue(fam.color, deg, 0.18),
    accent2: shiftHue(fam.color, deg2, 0.05),
    eyeColor: rng() < 0.5 ? accent : fam.color,
    pattern: Math.floor(rng() * 3),
    patternDensity: 3 + Math.floor(rng() * 3),
    spikes: 2 + Math.floor(rng() * 3),
    spikeStyle: Math.floor(rng() * 3),
    wingStyle: Math.floor(rng() * 3),
    crownStyle: Math.floor(rng() * 3),
    jitterX: 0.96 + rng() * 0.08,
    jitterY: 0.96 + rng() * 0.08,
    bodyR,
    bodyAspect: st.aspect * (0.94 + rng() * 0.12),
  }
}

/** Contour procédural: rayons par point autour du centre → silhouette unique. */
function bodyPath(cx: number, cy: number, r: number, radii: number[], aspect: number, smooth: boolean): string {
  const n = radii.length
  const pts: [number, number][] = []
  for (let i = 0; i < n; i++) {
    const ang = (i / n) * Math.PI * 2 - Math.PI / 2
    const rr = r * radii[i]
    pts.push([cx + Math.cos(ang) * rr * aspect, cy + Math.sin(ang) * rr])
  }
  const p = (x: number) => x.toFixed(1)
  if (!smooth) return 'M ' + pts.map(([x, y]) => `${p(x)} ${p(y)}`).join(' L ') + ' Z'
  const mid = (a: [number, number], b: [number, number]): [number, number] => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
  const m0 = mid(pts[n - 1], pts[0])
  let d = `M ${p(m0[0])} ${p(m0[1])}`
  for (let i = 0; i < n; i++) {
    const cur = pts[i]
    const nxt = pts[(i + 1) % n]
    const m = mid(cur, nxt)
    d += ` Q ${p(cur[0])} ${p(cur[1])} ${p(m[0])} ${p(m[1])}`
  }
  return d + ' Z'
}

/** Attributs distinctifs de la famille (cornes, oreilles, antenne…), teintés accent. */
function FamilyFeatures({ family, gene }: { family: Family; gene: Gene }) {
  const a = gene.accent
  const ad = gene.accentDark
  const cd = FAMILIES[family].colorDark
  switch (family) {
    case 'bitcoin':
      return (
        <g>
          <path d="M40 26 L34 10 L50 22 Z" fill={ad} stroke={cd} strokeWidth="1.5" />
          <path d="M80 26 L86 10 L70 22 Z" fill={ad} stroke={cd} strokeWidth="1.5" />
          <path d="M56 84 h8 M60 80 v12" stroke={a} strokeWidth="3.5" strokeLinecap="round" />
          <circle cx="34" cy="46" r="2.4" fill={a} />
          <circle cx="86" cy="46" r="2.4" fill={a} />
        </g>
      )
    case 'ethereum':
      return (
        <g>
          <path className="sparkle-pulse" d="M84 24 l3 6 l6 3 l-6 3 l-3 6 l-3 -6 l-6 -3 l6 -3 Z" fill={gene.accentLight} opacity="0.85" />
          <path d="M60 40 L70 60 L60 80 L50 60 Z" fill={ad} opacity="0.5" />
        </g>
      )
    case 'base':
      return (
        <g>
          <path d="M18 54 L2 46 L14 62 L0 64 L16 72 Z" fill={ad} />
          <path d="M102 54 L118 46 L106 62 L120 64 L104 72 Z" fill={ad} />
          <line x1="60" y1="18" x2="60" y2="5" stroke={ad} strokeWidth="3" strokeLinecap="round" />
          <circle cx="60" cy="4" r="3.5" fill={a} stroke={cd} strokeWidth="2" />
        </g>
      )
    case 'defi':
      return (
        <g>
          <path d="M32 30 L20 6 L48 22 Z" fill={a} stroke={cd} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M88 30 L100 6 L72 22 Z" fill={a} stroke={cd} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M50 86 L54 96 L58 86 Z" fill="#fff" />
          <path d="M62 86 L66 96 L70 86 Z" fill="#fff" />
        </g>
      )
    case 'meme':
      return (
        <g>
          <path d="M64 92 Q68 104 60 104 Q53 104 56 94" fill="#ff8fa3" stroke={cd} strokeWidth="2" />
          <path d="M52 16 Q56 3 62 13 Q68 3 72 16" stroke={ad} strokeWidth="3" fill="none" strokeLinecap="round" />
        </g>
      )
    case 'stable':
      return (
        <g opacity="0.9">
          <rect x="54" y="86" width="12" height="4.5" rx="2" fill={a} />
          <rect x="57.7" y="82.3" width="4.5" height="12" rx="2" fill={a} />
        </g>
      )
  }
}

function Eyes({ variant, color }: { variant: number; color: string }) {
  if (variant === 0) {
    return (
      <g>
        <circle cx="47" cy="55" r="7.5" fill="#0a0a10" />
        <circle cx="73" cy="55" r="7.5" fill="#0a0a10" />
        <circle cx="49" cy="53" r="2.6" fill="#fff" />
        <circle cx="75" cy="53" r="2.6" fill="#fff" />
      </g>
    )
  }
  if (variant === 1) {
    return (
      <g>
        <path d="M38 50 L54 56 L38 60 Z" fill="#0a0a10" />
        <path d="M82 50 L66 56 L82 60 Z" fill="#0a0a10" />
        <circle cx="45" cy="56" r="1.8" fill={color} />
        <circle cx="75" cy="56" r="1.8" fill={color} />
      </g>
    )
  }
  return (
    <g>
      <circle cx="60" cy="54" r="11" fill="#0a0a10" />
      <circle cx="60" cy="54" r="5" fill={color} />
      <circle cx="63" cy="51" r="2.2" fill="#fff" />
    </g>
  )
}

function Mouth({ variant }: { variant: number }) {
  if (variant === 0) {
    return <path d="M50 72 Q60 80 70 72" stroke="#0a0a10" strokeWidth="3" fill="none" strokeLinecap="round" />
  }
  if (variant === 1) {
    return <path d="M48 73 L54 69 L60 73 L66 69 L72 73" stroke="#0a0a10" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  }
  return (
    <g>
      <ellipse cx="60" cy="74" rx="7" ry="5" fill="#0a0a10" />
      <path d="M55 71 L58 74 L61 71" fill="#fff" />
    </g>
  )
}

// Corps PROCÉDURAL: silhouette unique (gene.bodyR) au style de la famille, plus
// ses attributs distinctifs (cornes, oreilles, antenne…) teintés accent.
function Body({ monster, bodyFill, gene }: { monster: Monster; bodyFill: string; gene: Gene }) {
  const family = monster.family
  const cd = FAMILIES[family].colorDark
  const st = BODY_STYLE[family]
  const d = bodyPath(60, 62, st.r, gene.bodyR, gene.bodyAspect, st.smooth)
  return (
    <g>
      <path d={d} fill={bodyFill} stroke={cd} strokeWidth="4" strokeLinejoin="round" />
      <FamilyFeatures family={family} gene={gene} />
    </g>
  )
}

// Motif de peau (épique+): pois / rayures / losanges (2e accent), densité variable.
function Pattern({ gene }: { gene: Gene }) {
  const c = gene.accent2
  if (gene.pattern === 0) {
    const dots: [number, number, number][] = [
      [46, 66, 3.4],
      [72, 70, 2.8],
      [58, 84, 3],
      [68, 50, 2.4],
      [50, 54, 2.2],
    ]
    return (
      <g fill={c} opacity="0.5">
        {dots.slice(0, gene.patternDensity).map(([x, y, r], i) => (
          <circle key={i} cx={x} cy={y} r={r} />
        ))}
      </g>
    )
  }
  if (gene.pattern === 1) {
    return (
      <g stroke={c} strokeWidth="3" opacity="0.42" strokeLinecap="round" fill="none">
        <path d="M40 60 Q60 66 80 60" />
        <path d="M42 74 Q60 80 78 74" />
        {gene.patternDensity >= 4 && <path d="M44 50 Q60 55 76 50" />}
      </g>
    )
  }
  const diamonds: [number, number][] = [
    [52, 62],
    [68, 68],
    [60, 82],
    [58, 52],
  ]
  return (
    <g fill={c} opacity="0.45">
      {diamonds.slice(0, gene.patternDensity - 1).map(([x, y], i) => (
        <path key={i} d={`M${x} ${y - 5} L${x + 5} ${y} L${x} ${y + 5} L${x - 5} ${y} Z`} />
      ))}
    </g>
  )
}

// Ailes (épique+) — derrière le corps ; 3 styles (anguleux / membraneux / plumeté).
// Une seule aile dessinée puis MIROIR autour de x=60 → symétrie garantie.
const WING_PATHS = [
  'M28 48 Q0 40 4 74 Q22 66 34 78 Q26 60 28 48 Z',
  'M30 46 Q4 46 2 74 L12 66 L15 77 L24 67 L30 78 Q30 60 30 46 Z',
  'M30 48 Q6 36 6 62 Q14 58 18 68 Q22 60 26 70 Q30 60 30 48 Z',
]
function Wings({ gene, big }: { gene: Gene; big: boolean }) {
  const s = big ? 1 : 0.85
  const d = WING_PATHS[gene.wingStyle]
  return (
    <g fill={gene.accentDark} opacity="0.9" transform={`translate(60 62) scale(${s}) translate(-60 -62)`}>
      <path d={d} stroke={gene.accent} strokeWidth="2" />
      <path d={d} stroke={gene.accent} strokeWidth="2" transform="translate(120 0) scale(-1 1)" />
    </g>
  )
}

// Pics dorsaux (dès le 1er stade) — nombre ET style variables selon le gène.
function Spikes({ gene, stage }: { gene: Gene; stage: number }) {
  if (stage < 1) return null
  const n = gene.spikes + (stage >= 2 ? 1 : 0)
  const items = []
  const startX = 60 - (n - 1) * 8
  const top = 6 - stage * 2
  for (let i = 0; i < n; i++) {
    const x = startX + i * 16
    const d =
      gene.spikeStyle === 0
        ? `M${x - 5} 20 L${x} ${top} L${x + 5} 20 Z` // pointu
        : gene.spikeStyle === 1
          ? `M${x - 5} 20 Q${x} ${top - 3} ${x + 5} 20 Z` // courbé
          : `M${x - 6} 20 L${x - 2} ${top} L${x + 2} ${top} L${x + 6} 20 Z` // trapu
    items.push(<path key={i} d={d} fill={gene.accent} stroke={gene.accentDark} strokeWidth="1.5" />)
  }
  return <g>{items}</g>
}

// Couronne (légendaire) — 3 styles (pointes / arrondi / royal).
const CROWN_PATHS = [
  'M42 20 L46 6 L54 16 L60 2 L66 16 L74 6 L78 20 Z',
  'M42 20 Q44 8 50 12 Q54 4 60 10 Q66 4 70 12 Q76 8 78 20 Z',
  'M40 20 L44 7 L52 14 L60 3 L68 14 L76 7 L80 20 Z',
]
function Crown({ gene }: { gene: Gene }) {
  return (
    <g>
      <path d={CROWN_PATHS[gene.crownStyle]} fill={gene.accent} stroke={gene.accentDark} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="60" cy="12" r="2.4" fill="#fff" />
    </g>
  )
}

// Gemme de poitrail (rare+).
function Gem({ gene }: { gene: Gene }) {
  return (
    <g transform="translate(60 78)">
      <path d="M0 -7 L6 0 L0 8 L-6 0 Z" fill={gene.accentLight} stroke={gene.accentDark} strokeWidth="1.5" />
      <path d="M0 -7 L0 8 M-6 0 L6 0" stroke={gene.accentDark} strokeWidth="0.8" opacity="0.6" />
    </g>
  )
}

// Jumeau client de MonsterRenderer.eggSvg(): œuf VRF en attente d'éclosion.
export function EggAvatar({ monster, size = 96 }: { monster: Monster; size?: number }) {
  const fam = FAMILIES[monster.family]
  return (
    <div className="anim-idle" style={{ width: size, height: size }}>
      <svg viewBox="0 0 120 120" width={size} height={size} aria-label="Œuf en éclosion">
        <path
          d="M60 20 Q90 44 90 72 A30 34 0 0 1 30 72 Q30 44 60 20 Z"
          fill={fam.colorDark}
          stroke={fam.color}
          strokeWidth="3"
        />
        <path
          d="M46 62 L54 70 L60 60 L66 72 L74 62"
          stroke={fam.color}
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

// Ornements de métamorphose flottants — halo, anneau d'orbite, couronne d'étoiles.
function StageAdornments({ stage, color }: { stage: number; color: string }) {
  if (stage < 1) return null
  return (
    <g>
      <ellipse cx="60" cy="114" rx="36" ry="4.5" fill={color} opacity="0.22" />
      <path d="M6 56 l4.5 -8 l4.5 8 l-4.5 8 Z" fill={color} opacity="0.85" />
      <path d="M105 56 l4.5 -8 l4.5 8 l-4.5 8 Z" fill={color} opacity="0.85" />
      {stage >= 2 && (
        <g>
          <circle cx="60" cy="60" r="58" fill="none" stroke={color} strokeWidth="1.5" opacity="0.4" strokeDasharray="7 11" />
          <path d="M14 14 l2 4 l4 2 l-4 2 l-2 4 l-2 -4 l-4 -2 l4 -2 Z" fill="#fff" opacity="0.9" />
          <path d="M106 14 l2 4 l4 2 l-4 2 l-2 4 l-2 -4 l-4 -2 l4 -2 Z" fill="#fff" opacity="0.9" />
        </g>
      )}
    </g>
  )
}

export default function MonsterAvatar({ monster, size = 96, animated = true, className = '' }: Props) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  if (monster.hatching) return <EggAvatar monster={monster} size={size} />

  const fam = FAMILIES[monster.family]
  const gene = makeGene(monster.seed, monster.family)
  const stage = stageOf(monster.level ?? 1, evolutionsOf(monster))
  const rlvl = RARITY_LEVEL[monster.rarity]
  const blinkDelay = mulberry32(hashSeed(monster.seed + ':blink'))() * 4
  const wobble = monster.family === 'meme' && animated && size >= 64

  // rareté → ornements ; l'évolution grossit le corps de façon cohérente
  const showGem = rlvl >= 1
  const showPattern = rlvl >= 2
  const showWings = rlvl >= 2
  const showCrown = rlvl >= 3
  const showAura = rlvl >= 2 || stage >= 1
  // L'évolution grossit le corps ; jitterX/Y donnent des proportions propres.
  const st0 = [1, 1.07, 1.14][stage]
  const sx = (st0 * gene.jitterX).toFixed(3)
  const sy = (st0 * gene.jitterY).toFixed(3)

  const glow =
    stage >= 2
      ? 'glow-apex'
      : monster.rarity === 'legendary'
        ? 'glow-legendary'
        : monster.rarity === 'epic'
          ? 'glow-epic'
          : ''

  return (
    <div
      className={`${animated ? 'anim-idle' : ''} ${glow} ${className}`}
      style={{ width: size, height: size, animationDuration: animated ? IDLE_DURATION[monster.family] : undefined }}
    >
      <svg viewBox="0 0 120 120" width={size} height={size} aria-label={monster.name}>
        <defs>
          <radialGradient id={`g${uid}`} cx="38%" cy="30%" r="80%">
            <stop offset="0%" stopColor={fam.colorLight} />
            <stop offset="50%" stopColor={fam.color} />
            {/* teinte le bord vers la couleur d'accent → palette unique par monstre */}
            <stop offset="100%" stopColor={gene.accentDark} stopOpacity="0.95" />
          </radialGradient>
          {showAura && (
            <radialGradient id={`a${uid}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={gene.accent} stopOpacity="0.5" />
              <stop offset="70%" stopColor={gene.accent} stopOpacity="0.12" />
              <stop offset="100%" stopColor={gene.accent} stopOpacity="0" />
            </radialGradient>
          )}
          {wobble && (
            <filter id={`w${uid}`} x="-20%" y="-20%" width="140%" height="140%">
              <feTurbulence type="fractalNoise" baseFrequency="0.014 0.022" numOctaves="1" seed="7" result="n">
                <animate attributeName="baseFrequency" dur="6s" values="0.014 0.022;0.02 0.03;0.014 0.022" repeatCount="indefinite" />
              </feTurbulence>
              <feDisplacementMap in="SourceGraphic" in2="n" scale="4" />
            </filter>
          )}
        </defs>

        {showAura && <circle cx="60" cy="60" r="58" fill={`url(#a${uid})`} />}
        <StageAdornments stage={stage} color={gene.accent} />
        {showWings && <Wings gene={gene} big={stage >= 1} />}

        {/* Corps mis à l'échelle (stade + proportions propres au monstre) */}
        <g transform={`translate(60 62) scale(${sx} ${sy}) translate(-60 -62)`}>
          <Spikes gene={gene} stage={stage} />
          <g filter={wobble ? `url(#w${uid})` : undefined}>
            <Body monster={monster} bodyFill={`url(#g${uid})`} gene={gene} />
            {showPattern && <Pattern gene={gene} />}
            {showGem && <Gem gene={gene} />}
          </g>
          {animated ? (
            <g className="eyes-blink" style={{ animationDelay: `${blinkDelay.toFixed(2)}s` }}>
              <Eyes variant={gene.eye} color={gene.eyeColor} />
            </g>
          ) : (
            <Eyes variant={gene.eye} color={gene.eyeColor} />
          )}
          <Mouth variant={gene.mouth} />
        </g>

        {showCrown && <Crown gene={gene} />}
      </svg>
    </div>
  )
}
